import { useEffect, useState } from "react";

import { MatrixHttpClient } from "@/lib/matrix-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Query keys
export const matrixKeys = {
	all: ["matrix"] as const,
	devices: () => [...matrixKeys.all, "devices"] as const,
	device: (deviceId: string) => [...matrixKeys.devices(), deviceId] as const,
	rooms: () => [...matrixKeys.all, "rooms"] as const,
	room: (roomId: string) => [...matrixKeys.rooms(), roomId] as const,
	messages: (roomId: string) => [...matrixKeys.room(roomId), "messages"] as const,
	profile: (userId: string) => [...matrixKeys.all, "profile", userId] as const,
	directMessages: () => [...matrixKeys.all, "directMessages"] as const
};

// Hook to initialize MatrixHttpClient
export function useMatrixClient(homeServerUrl: string, accessToken: string, userId: string) {
	const [chatInstance, setChatInstance] = useState<MatrixHttpClient | null>(null);

	useEffect(() => {
		if (chatInstance)
			return;
		setChatInstance(new MatrixHttpClient(homeServerUrl, accessToken, userId));
	}, [accessToken, chatInstance, homeServerUrl, userId]);

	return chatInstance;
}

// Devices
export function useDevices(client: MatrixHttpClient) {
	return useQuery({
		queryKey: matrixKeys.devices(),
		queryFn: () => client.getDevices()
	});
}

export function useDevice(client: MatrixHttpClient, deviceId: string) {
	return useQuery({
		queryKey: matrixKeys.device(deviceId),
		queryFn: () => client.getDevice(deviceId)
	});
}

export function useUpdateDevice(client: MatrixHttpClient) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ deviceId, displayName }: { deviceId: string; displayName: string }) =>
			client.updateDevice(deviceId, displayName),
		onSuccess: (_, { deviceId }) => {
			queryClient.invalidateQueries({ queryKey: matrixKeys.device(deviceId) });
			queryClient.invalidateQueries({ queryKey: matrixKeys.devices() });
		}
	});
}

export function useDeleteDevice(client: MatrixHttpClient) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ deviceId, authData }: { deviceId: string; authData?: any }) =>
			client.deleteDevice(deviceId, authData),
		onSuccess: (_) => {
			queryClient.invalidateQueries({ queryKey: matrixKeys.devices() });
		}
	});
}

// Rooms
export function useJoinedRooms(client: MatrixHttpClient) {
	return useQuery({
		queryKey: matrixKeys.rooms(),
		queryFn: () => client.getJoinedRooms(),
		enabled: !!client
	});
}

export function useRoomMessages(client: MatrixHttpClient, roomId: string, options: {
	from?: string;
	to?: string;
	dir?: "b" | "f";
	limit?: number;
	filter?: string;
} = {}) {
	return useQuery({
		queryKey: [...matrixKeys.messages(roomId), options],
		queryFn: () => client.getRoomMessages(roomId, options),
		enabled: !!client && !!roomId
	});
}

export function useRecentMessages(client: MatrixHttpClient, roomId: string, limit: number = 50) {
	return useQuery({
		queryKey: [...matrixKeys.messages(roomId), { limit }],
		queryFn: () => client.getRecentMessages(roomId, limit),
		enabled: !!client && !!roomId,
		retry: false
	});
}

export function useSendMessage(client: MatrixHttpClient | null) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			roomId,
			message,
			msgType = "m.text"
		}: {
			roomId: string;
			message: string;
			msgType?: string;
		}) => {
			if (!client)
				return null;
			return await client.sendMessage(roomId, message, msgType);
		},
		onSuccess: (_, { roomId }) => {
			queryClient.invalidateQueries({ queryKey: matrixKeys.messages(roomId) });
		}

	});
}

export function useSendFormattedMessage(client: MatrixHttpClient) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			roomId,
			text,
			formattedText
		}: {
			roomId: string;
			text: string;
			formattedText: string;
		}) => client.sendFormattedMessage(roomId, text, formattedText),
		onSuccess: (_, { roomId }) => {
			queryClient.invalidateQueries({ queryKey: matrixKeys.messages(roomId) });
		}
	});
}

// Room Creation and Management
export function useCreateRoom(client: MatrixHttpClient | null) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			invite,
			invite_name
		}: {
			invite: string[];
			invite_name: string;
		}) => {
			if (!client)
				return null;
			return await client.createRoom(invite, invite_name);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: matrixKeys.rooms() });
		}
	});
}

export function useJoinRoom(client: MatrixHttpClient) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ roomId, reason }: { roomId: string; reason?: string }) =>
			client.joinRoom(roomId, reason),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: matrixKeys.rooms() });
		}
	});
}

export function useLeaveRoom(client: MatrixHttpClient) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ roomId, reason }: { roomId: string; reason?: string }) =>
			client.leaveRoom(roomId, reason),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: matrixKeys.rooms() });
		}
	});
}

// User Profile
export function useUserProfile(client: MatrixHttpClient, userId?: string) {
	return useQuery({
		queryKey: matrixKeys.profile(userId || client.getUserId()),
		queryFn: () => client.getUserProfile(userId)
	});
}

export function useSetDisplayName(client: MatrixHttpClient) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (displayName: string) => client.setDisplayName(displayName),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: matrixKeys.profile(client.getUserId())
			});
		}
	});
}

// Direct Messages
export function useDirectMessageRooms(client: MatrixHttpClient) {
	return useQuery({
		queryKey: matrixKeys.directMessages(),
		queryFn: () => client.getDirectMessageRooms()
	});
}

// Typing Notifications
export function useSendTypingNotification(client: MatrixHttpClient) {
	return useMutation({
		mutationFn: ({
			roomId,
			isTyping,
			timeoutMs = 30000
		}: {
			roomId: string;
			isTyping: boolean;
			timeoutMs?: number;
		}) => client.sendTypingNotification(roomId, isTyping, timeoutMs)
	});
}

// Mark as Read
export function useMarkRoomAsRead(client: MatrixHttpClient) {
	return useMutation({
		mutationFn: ({
			roomId,
			eventId
		}: {
			roomId: string;
			eventId: string;
		}) => client.markRoomAsRead(roomId, eventId)
	});
}
